import { useEffect } from 'react';

/** Pixels per line for wheel events in line mode (Firefox) */
const LINE_HEIGHT = 16;

/**
 * Sync scroll between a scrollable container and window.
 * - Scroll goes to window until window reaches bottom.
 * - Once window is at bottom, scroll goes to the container.
 * - When scrolling up and the container is at top, scroll can go back to window.
 * Horizontal and zoom (ctrl) gestures, and wheels over a nested scrollable area, are left untouched.
 * @param {React.RefObject<HTMLElement>} scrollContainerRef - Ref to the scrollable container
 * @param {boolean} [enabled=true] - Attach the listeners (the container must be rendered)
 * @returns {void}
 */
export function useSyncScrollWithWindow(scrollContainerRef, enabled = true) {
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!enabled || !el) return undefined;

    /**
     * Whether a scrollable element between the target and the container can scroll vertically.
     * @param {EventTarget | null} target
     * @returns {boolean}
     */
    const isInNestedScrollable = (target) => {
      let node = target instanceof Element ? target : null;
      while (node && node !== el) {
        const { overflowY } = getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return true;
        node = node.parentElement;
      }
      return false;
    };

    /**
     * Handle wheel event (mouse wheel / trackpad)
     * @param {WheelEvent} event
     */
    const onWheel = (event) => {
      if (event.deltaY === 0 || event.ctrlKey || event.shiftKey) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (isInNestedScrollable(event.target)) return;

      // deltaMode: 0 = pixels, 1 = lines, 2 = pages
      const deltaUnit = [1, LINE_HEIGHT, window.innerHeight][event.deltaMode] ?? 1;
      const deltaY = event.deltaY * deltaUnit;
      // Diagonal trackpad gesture: the cancelled event must not lose its horizontal part
      const forwardHorizontal = () => {
        if (event.deltaX) el.scrollLeft += event.deltaX * deltaUnit;
      };

      const scrollingDown = deltaY > 0;
      const scrollingUp = deltaY < 0;

      const doc = document.documentElement;
      const windowScrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = doc.scrollHeight;

      const windowAtTop = windowScrollY <= 0;
      const windowAtBottom = windowScrollY + windowHeight >= docHeight - 1; // small epsilon

      const elScrollTop = el.scrollTop;
      const elAtTop = elScrollTop <= 0;

      if (scrollingDown && !windowAtBottom) {
        event.preventDefault();
        window.scrollBy({ top: deltaY, behavior: 'auto' });
        forwardHorizontal();
        return;
      }

      if (scrollingUp && elAtTop && !windowAtTop) {
        event.preventDefault();
        window.scrollBy({ top: deltaY, behavior: 'auto' });
        forwardHorizontal();
      }
    };

    /**
     * Handle touch events for mobile
     */
    let touchStartY = 0;
    let touchStartWindowScroll = 0;

    const onTouchStart = (/** @type {TouchEvent} */ event) => {
      touchStartY = event.touches[0].clientY;
      touchStartWindowScroll = window.scrollY || window.pageYOffset;
    };

    const onTouchMove = (/** @type {TouchEvent} */ event) => {
      const touchY = event.touches[0].clientY;
      const deltaY = touchStartY - touchY; // Positive = scrolling down
      const scrollingDown = deltaY > 0;
      const scrollingUp = deltaY < 0;

      const doc = document.documentElement;
      const windowScrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = doc.scrollHeight;

      const windowAtTop = windowScrollY <= 0;
      const windowAtBottom = windowScrollY + windowHeight >= docHeight - 1;

      const elScrollTop = el.scrollTop;
      const elAtTop = elScrollTop <= 0;

      if (scrollingDown && !windowAtBottom) {
        event.preventDefault();
        window.scrollTo({
          top: touchStartWindowScroll + deltaY,
          behavior: 'auto',
        });
        return;
      }

      if (scrollingUp && elAtTop && !windowAtTop) {
        event.preventDefault();
        window.scrollTo({
          top: touchStartWindowScroll + deltaY,
          behavior: 'auto',
        });
      }
    };

    // @ts-ignore - passive option is valid but not in all type definitions
    el.addEventListener('wheel', onWheel, { passive: false });
    // @ts-ignore
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    // @ts-ignore
    el.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      // @ts-ignore
      el.removeEventListener('wheel', onWheel, { passive: false });
      // @ts-ignore
      el.removeEventListener('touchstart', onTouchStart, { passive: true });
      // @ts-ignore
      el.removeEventListener('touchmove', onTouchMove, { passive: false });
    };
  }, [scrollContainerRef, enabled]);
}

export default useSyncScrollWithWindow;
